import {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {TablerPlayerPlay, TablerX} from '../../icons';
import usePlannerStyles from '../planner/usePlannerStyles';
import {captureUniversity} from './universityAnalytics';
import useUniversity from './useUniversity';
import UniversityVideoModal from './UniversityVideoModal';

const GETTING_STARTED_LABEL = 'Getting Started';

// ─── Getting Started progress card (Planner Home) ────────────────────────────
//
// The persistent guided-onboarding surface for users who signed in via
// /start: playlist progress plus a one-tap "watch next" CTA, pinned with the
// planner banners. The backend decides visibility (guided user, playlist not
// complete, not dismissed), so this renders nothing for everyone else.
// Follows PlannerContinueSessionBanner's quiet-panel look: neutral surface,
// opalite reserved for the action.
const UniversityGettingStartedCard = () => {
  const styles = usePlannerStyles();
  const {
    guided,
    playlistVideos,
    isWatched,
    markWatched,
    dismissGettingStarted,
  } = useUniversity();
  const [playerIndex, setPlayerIndex] = useState<number | null>(null);

  if (!guided.showGettingStartedCard || playlistVideos.length === 0) {
    return null;
  }

  const watchedCount = playlistVideos.filter(video =>
    isWatched(video.id),
  ).length;
  const nextIndex = playlistVideos.findIndex(video => !isWatched(video.id));
  const nextVideo = playlistVideos[Math.max(nextIndex, 0)];
  const progressRatio = watchedCount / playlistVideos.length;

  useEffect(() => {
    captureUniversity('university_getting_started_card_shown', {
      watched_count: watchedCount,
      playlist_total: playlistVideos.length,
      next_video_id: nextVideo.id,
    });
    // Once per mount while the card is visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        data-testid="university-getting-started-card"
        position="relative"
        flex="0 0 auto"
        bg={styles.surfaceBg}
        borderWidth="1px"
        borderColor={styles.borderColor}
        borderRadius="lg"
        overflow="hidden"
        px={4}
        py={3}
      >
        <Flex align="center" justify="space-between" gap={3} minW={0}>
          <Box minW={0} flex="1 1 auto">
            <Text
              fontSize="xs"
              fontWeight="medium"
              color={styles.metaTextColor}
              textTransform="uppercase"
              letterSpacing="0.04em"
              lineHeight="1.2"
              whiteSpace="nowrap"
            >
              {GETTING_STARTED_LABEL} · {watchedCount} of{' '}
              {playlistVideos.length} watched
            </Text>
            <Text
              fontSize="md"
              fontWeight="semibold"
              color={styles.textColor}
              lineHeight="1.25"
              mt={1}
              noOfLines={1}
            >
              {nextVideo.title}
            </Text>
            <Box
              mt={2}
              h="4px"
              w="100%"
              maxW="220px"
              bg={styles.trackBg}
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                h="100%"
                w={`${Math.round(progressRatio * 100)}%`}
                bg="opalite.500"
                borderRadius="full"
                transition="width 0.3s ease"
              />
            </Box>
          </Box>
          <HStack spacing={1} flexShrink={0}>
            <Button
              type="button"
              size="sm"
              leftIcon={<TablerPlayerPlay size={14} aria-hidden="true" />}
              minH={{base: '44px', md: '36px'}}
              px={3}
              borderRadius="lg"
              bg="opalite.600"
              color="white"
              fontSize="sm"
              fontWeight="medium"
              lineHeight="1.25"
              onClick={() => {
                captureUniversity('university_getting_started_card_opened', {
                  next_video_id: nextVideo.id,
                  next_video_title: nextVideo.title,
                  watched_count: watchedCount,
                  playlist_total: playlistVideos.length,
                });
                setPlayerIndex(Math.max(nextIndex, 0));
              }}
              aria-label={`Watch ${nextVideo.title}`}
              _hover={{bg: 'opalite.700', color: 'white'}}
              _active={{bg: 'opalite.700'}}
              _focusVisible={styles.focusOutlineStyles}
            >
              Watch
            </Button>
            <Tooltip label="Dismiss" hasArrow openDelay={300}>
              <IconButton
                type="button"
                aria-label="Dismiss getting started videos"
                icon={<TablerX size={16} aria-hidden="true" />}
                size="sm"
                variant="ghost"
                borderRadius="lg"
                minW={{base: '44px', md: '36px'}}
                minH={{base: '44px', md: '36px'}}
                color={styles.metaTextColor}
                onClick={dismissGettingStarted}
                _hover={{bg: styles.rowHoverBg}}
                _active={{bg: styles.rowHoverBg}}
                _focusVisible={styles.focusOutlineStyles}
              />
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      {playerIndex !== null && (
        <UniversityVideoModal
          isOpen
          onClose={() => setPlayerIndex(null)}
          videos={playlistVideos}
          initialIndex={playerIndex}
          playlistLabel={GETTING_STARTED_LABEL}
          source="getting_started_card"
          onWatched={markWatched}
        />
      )}
    </>
  );
};

export default UniversityGettingStartedCard;
